// frontend/src/components/common/MobileTabBar.js
//
// Bottom tab bar for the Manager Portal on mobile web — replaces the old
// slide-in burger drawer (see PortalLayout.js). Renders 4 primary tabs plus a
// "More" tab that opens a full-screen sheet listing the complete nav, grouped
// exactly like the desktop sidebar.
//
// Contract: <MobileTabBar groups={filteredNavGroups} /> — the caller (PortalLayout)
// passes the SAME permission-filtered nav groups it renders in the sidebar, with
// each item annotated with `featureLocked` (bool). This component never re-derives
// or loosens that gating — it only lays out what it's given.
//
// The sidebar (desktop only) carries its own Sign out control and the topbar help
// icon; neither exists on mobile, so the More sheet adds explicit rows for both:
// - `onSignOut` — handler invoked when the Sign out row is tapped (sheet closes first).
// - `helpCenterAllowed` — bool the caller computes from the SAME permission gate the
//   old Help Center nav item used (NAV_PERMISSION_MAP['help-center']); the Help Center
//   row only renders when this is explicitly true, matching the "never loosens gating"
//   rule above.
//
// Shared under components/common/ so the F12 staff/portal apps can reuse the
// same pattern with their own nav data.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { C, FONT } from '../../styles/theme';

export const MOBILE_TAB_BAR_HEIGHT = 56;

const ICONS = {
    overview: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>),
    reporting: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>),
    employees: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
    products: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>),
    more: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>),
};

// Primary tabs, in display order. `navId` is the id of the sidebar nav item
// that gates this tab's visibility (item must be present in `groups`).
const PRIMARY_TABS = [
    { navId: 'overview', labelKey: 'tab_home', fallback: 'Home', path: '/portal/overview', match: (p) => p.startsWith('/portal/overview') },
    { navId: 'reporting', labelKey: 'tab_analytics', fallback: 'Analytics', path: '/portal/reporting', match: (p) => p.startsWith('/portal/reporting') },
    { navId: 'employees', labelKey: 'tab_team', fallback: 'Team', path: '/portal/employees', match: (p) => p.startsWith('/portal/employees') || p.startsWith('/portal/schedule') || p.startsWith('/portal/payrun') },
    { navId: 'products', labelKey: 'tab_catalogue', fallback: 'Catalogue', path: '/portal/products', match: (p) => p.startsWith('/portal/products') || p.startsWith('/portal/inventory') },
];

export default function MobileTabBar({ groups = [], onSignOut, helpCenterAllowed }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    const [moreOpen, setMoreOpen] = useState(false);

    const allItems = groups.flatMap(g => g.items);
    const findItem = (id) => allItems.find(i => i.id === id);

    // Escape closes the More sheet
    useEffect(() => {
        if (!moreOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [moreOpen]);

    // Close the sheet whenever the route changes (covers back/forward nav too)
    useEffect(() => { setMoreOpen(false); }, [location.pathname]);

    const visibleTabs = PRIMARY_TABS.filter(tab => findItem(tab.navId));
    const activeTab = visibleTabs.find(tab => tab.match(location.pathname));

    const go = (path) => { setMoreOpen(false); navigate(path); };

    return (
        <>
            <nav
                role="tablist"
                aria-label={t('menu', {}, 'Menu')}
                style={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900,
                    display: 'flex', background: C.white, borderTop: `1px solid ${C.border}`,
                    paddingBottom: 'env(safe-area-inset-bottom)', fontFamily: FONT,
                }}
            >
                {visibleTabs.map(tab => {
                    const isActive = tab === activeTab;
                    return (
                        <button
                            key={tab.navId}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => go(tab.path)}
                            style={{
                                flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', gap: 2, minHeight: MOBILE_TAB_BAR_HEIGHT, minWidth: 44,
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                color: isActive ? C.greenDark : C.mutedLight,
                            }}
                        >
                            {ICONS[tab.navId]}
                            <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                                {t(tab.labelKey, {}, tab.fallback)}
                            </span>
                        </button>
                    );
                })}
                <button
                    role="tab"
                    aria-selected={!activeTab}
                    onClick={() => setMoreOpen(true)}
                    style={{
                        flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 2, minHeight: MOBILE_TAB_BAR_HEIGHT, minWidth: 44,
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        color: (!activeTab || moreOpen) ? C.greenDark : C.mutedLight,
                    }}
                >
                    {ICONS.more}
                    <span style={{ fontSize: 10, fontWeight: (!activeTab || moreOpen) ? 700 : 500, lineHeight: 1 }}>
                        {t('tab_more', {}, 'More')}
                    </span>
                </button>
            </nav>

            {moreOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('menu', {}, 'Menu')}
                    style={{ position: 'fixed', inset: 0, zIndex: 1000, background: C.white, display: 'flex', flexDirection: 'column', fontFamily: FONT }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>{t('menu', {}, 'Menu')}</h2>
                        <button
                            onClick={() => setMoreOpen(false)}
                            aria-label={t('close', {}, 'Close')}
                            title={t('close', {}, 'Close')}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 44, height: 44, background: 'none', border: 'none', cursor: 'pointer',
                                color: C.text, borderRadius: 8, flexShrink: 0,
                            }}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
                        {groups.map(group => (
                            <div key={group.label}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: C.mutedLight, letterSpacing: 2, textTransform: 'uppercase', padding: '16px 20px 6px' }}>
                                    {t(group.label)}
                                </div>
                                {group.items.map(item => {
                                    const isCurrent = location.pathname.startsWith(item.path);
                                    return (
                                        <div
                                            key={item.id}
                                            role="button"
                                            onClick={() => { if (!item.disabled) go(item.path); }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                                                minHeight: 44, cursor: item.disabled ? 'default' : 'pointer',
                                                background: isCurrent ? C.greenLight : 'transparent',
                                                color: isCurrent ? C.greenDark : item.disabled ? C.mutedLight : C.text,
                                            }}
                                        >
                                            <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
                                            <span style={{ fontSize: 14, fontWeight: isCurrent ? 700 : 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                {t(item.label)}
                                                {item.disabled && <span style={{ fontSize: 8, color: C.mutedLight, fontWeight: 600, letterSpacing: 0.5 }}>{t('soon_badge')}</span>}
                                                {item.featureLocked && !item.disabled && (
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: C.mutedLight, flexShrink: 0 }}>
                                                        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                                    </svg>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                        {/* Help Center — old SUPPORT nav item was removed from the sidebar
                            (topbar help icon covers desktop); this is its mobile replacement. */}
                        {helpCenterAllowed && (
                            <div
                                role="button"
                                onClick={() => go('/portal/help-center')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                                    minHeight: 44, cursor: 'pointer', borderTop: `1px solid ${C.border}`,
                                    background: location.pathname.startsWith('/portal/help-center') ? C.greenLight : 'transparent',
                                    color: location.pathname.startsWith('/portal/help-center') ? C.greenDark : C.text,
                                }}
                            >
                                <span style={{ flexShrink: 0, display: 'flex' }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                </span>
                                <span style={{ fontSize: 14, fontWeight: 500 }}>{t('Help Center')}</span>
                            </div>
                        )}

                        {/* Sign out — sidebar's Sign out control is desktop-only, so the
                            More sheet needs its own entry point, closed off from the rest. */}
                        <div
                            role="button"
                            onClick={() => { setMoreOpen(false); if (onSignOut) onSignOut(); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                                minHeight: 44, cursor: 'pointer', color: C.red, borderTop: `1px solid ${C.border}`,
                            }}
                        >
                            <span style={{ flexShrink: 0, display: 'flex' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 500 }}>{t('sign_out')}</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
